import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorInfo } from '../../../../api/model';
import { PostPrivilegesUpdateBody } from '../../../../api/model/postPrivilegesUpdateBody';
import { PrivilegeItemList } from '../../../../api/model/privilegeItemList';
import { useGetPrivilegesId, usePostPrivilegesUpdate } from '../../../../api/privilege/privilege';
import { useDefaultOptions } from '../../../../hooks/api';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';
import { SelectGroupAccessAndAuthority } from './FormElements';

export type FieldValues = Record<string, any>;

interface EditAreaProps {
  privilegeId: number;
  screenParams: PrivilegeItemList;
  Editable: boolean;
}

/**
 * タブタイトル
 * @returns
 */
function Title() {
  // 言語情報
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>{t('CMS-POC|権限情報')}</title>
    </Helmet>
  );
}

export function EditArea({ privilegeId, screenParams, Editable }: EditAreaProps) {
  // const { t } = useTranslation();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  const { register, handleSubmit, reset } = useForm<PrivilegeItemList>({ defaultValues: { ...screenParams } });

  const navigate = useNavigate();

  // [元に戻す]ボタン
  const onReset = useCallback(() => setShowResetModalFlg(true), []);
  const onCancel = useCallback(() => navigate(`..`), [navigate]);

  // [元に戻す]確認モーダル内 [OK]ボタン
  const [showResetModalFlg, setShowResetModalFlg] = useState(false);
  const onClickResetOk = useCallback(() => {
    reset();
    setShowResetModalFlg(false);
  }, [reset]);

  const onUpdate = useCallback(() => setShowUpdateModalFlg(true), []); // [更新]ボタン
  const [showUpdateModalFlg, setShowUpdateModalFlg] = useState(false); // [更新]確認モーダル内 [OK]ボタン
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false); // submit中
  const [DisplayPutError, setDisplayPutError] = useState<boolean>(false); // 更新エラー
  const [DisplayPutOk, setDisplayPutOk] = useState<boolean>(false); // 更新エラー

  // 更新
  const { mutateAsync: putAsync, error: putError } = usePostPrivilegesUpdate<AxiosError<ErrorInfo>>(options);
  useEffect(() => {
    setDisplayPutError(putError != null);
    setIsSubmitting(false);
  }, [putError]);

  const onSubmit = useCallback(
    () =>
      handleSubmit(async (data: FieldValues) => {
        setIsSubmitting(true);
        const body: PostPrivilegesUpdateBody = {};
        body.privilege_detail = data;
        await putAsync(
          {
            privilegeId: Number(privilegeId),
            data: body,
          },
          {
            onSuccess: () => {
              reset(data);
            },
          }
        );
        setIsSubmitting(false);
      })(),
    [putAsync, privilegeId, handleSubmit, reset]
  );

  return (
    <>
      <section>
        <Title />
        <fieldset disabled={isSubmitting} className="fieldset-reset">
          <form>
            <h3 className="">
              <span>{t('権限情報')}</span>
            </h3>
            <div className="wfr">
              <div className="grid3 labip4 mb20">
                <div>
                  <label htmlFor="my3-1">{t('権限名')}</label>
                  <input type="text" {...register(`privilege_name`)} readOnly />
                </div>
              </div>
              <div className="authorityfr mb30">
                <div className="ttl">
                  <p>
                    {t('機能別')}
                    <br />
                    {t('アクセス制限')}
                  </p>
                </div>
                <div className="cont1">
                  <table className="table44">
                    <tbody>
                      <tr className="line">
                        <th>{t('機能')}</th>
                        <th>{t('権限')}</th>
                        <th aria-label=" " />
                      </tr>
                      <tr className="line">
                        <th>{t('Home')}</th>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${0}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${0}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line noselect">
                        <th>{t('制作一覧')}</th>
                        <td />
                        <td />
                      </tr>
                      <tr>
                        <td>{t('制作一覧')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${1}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${1}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('一覧出力')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${2}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${2}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line">
                        <td>{t('新規資料追加')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${3}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${3}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line noselect">
                        <th>{t('制作管理画面')}</th>
                        <td />
                        <td />
                      </tr>
                      <tr>
                        <td>{t('制作管理表編集')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${4}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${4}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('発注書出力')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${5}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${5}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('F-list取込み')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${6}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${6}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('日程編集')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${7}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${7}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('トス資料')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${8}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${8}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('確認依頼')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${9}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${9}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('原稿タブ')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${10}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${10}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('原稿 F-List判定結果')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${11}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${11}.privilege_setting`}
                          register={register}
                        />
                      </tr>

                      <tr>
                        <td>{t('懸案項目')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${22}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${22}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('懸案項目出力')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${23}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${23}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('翻訳')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${24}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${24}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('翻訳 インポート')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${25}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${25}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('翻訳 エクスポート')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${26}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${26}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('翻訳 ベース言語同期')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${27}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${27}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('承認')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${28}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${28}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line">
                        <td>{t('承認(確認者)')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${29}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${29}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line noselect">
                        <th>{t('原稿編集')}</th>
                        <td />
                        <td />
                      </tr>
                      <tr>
                        <td>{t('原稿編集')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${14}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${14}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('ReqSPEC連携')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${15}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${15}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('PS改廃案登録')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${16}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${16}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('OM,WebOMプレビュー')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${17}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${17}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('Validation設定')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${18}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${18}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('原稿編集画面(PS情報)')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${19}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${19}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('原稿編集画面(原稿データ検索)')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${20}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${20}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line">
                        <td>{t('原稿編集画面(PS改廃リスト)')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${21}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${21}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                    </tbody>
                  </table>
                  <div className="postscript">
                    <p>{t('〇：編集/閲覧可、△：閲覧のみ、×：閲覧不可')}</p>
                    <p>
                      {t('*0：フルアクセス権限、*1：自社および担当データに限る、*2：共有先に指定されているものに限る')}
                    </p>
                  </div>
                </div>
                <div className="cont2">
                  <table className="table44">
                    <tbody>
                      <tr className="line">
                        <th>{t('機能')}</th>
                        <th>{t('権限')}</th>
                        <th aria-label=" " />
                      </tr>
                      <tr className="line noselect">
                        <th>{t('ベース原稿確認')}</th>
                        <td />
                        <td />
                      </tr>
                      <tr>
                        <td>{t('判定結果修正')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${12}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${12}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line">
                        <td>{t('原稿 F-List判定結果出力')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${13}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${13}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line noselect">
                        <th>{t('日程一覧')}</th>
                        <td />
                        <td />
                      </tr>
                      <tr>
                        <td>{t('日程一覧')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${33}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${33}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line">
                        <td>{t('日程 エクスポート')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${34}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${34}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line noselect">
                        <th>{t('PS一覧')}</th>
                        <td />
                        <td />
                      </tr>
                      <tr>
                        <td>{t('PS一覧')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${35}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${35}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('PS改廃案一覧')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${36}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${36}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('図面リスト出力')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${37}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${37}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('承認機能(確認者)')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${43}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${43}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line">
                        <td>{t('承認機能')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${44}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${44}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line noselect">
                        <th>{t('PS閲覧')}</th>
                        <td />
                        <td />
                      </tr>
                      <tr>
                        <td>{t('PS閲覧')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${38}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${38}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('PS出力')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${39}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${39}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line">
                        <td>{t('原作用PS出力')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${40}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${40}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line noselect">
                        <th>{t('PS編集')}</th>
                        <td />
                        <td />
                      </tr>
                      <tr>
                        <td>{t('PS編集')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${41}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${41}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line">
                        <td>{t('改廃案保存')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${42}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${42}.privilege_setting`}
                          register={register}
                        />
                      </tr>

                      <tr className="line noselect">
                        <th>{t('イラスト一覧')}</th>
                        <td />
                        <td />
                      </tr>

                      <tr>
                        <td>{t('イラスト一覧')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${30}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${30}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('不要イラスト削除')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${31}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${31}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line">
                        <td>{t('イラスト編集')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${32}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${32}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr className="line noselect">
                        <th>{t('システム管理')}</th>
                        <td />
                        <td />
                      </tr>
                      <tr>
                        <td>{t('マスタ管理機能')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${45}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${45}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('BMSインポート')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${46}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${46}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('ユーザー管理機能')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${51}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${51}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('ユーザー編集設定')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${47}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${47}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('マニュアル')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${48}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${48}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('グループ管理機能')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${49}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${49}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                      <tr>
                        <td>{t('権限管理機能')}</td>
                        <SelectGroupAccessAndAuthority
                          nameAccess={`privilege_itemlist_items.${50}.accsess_restriction`}
                          nameAuthority={`privilege_itemlist_items.${50}.privilege_setting`}
                          register={register}
                        />
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="formarea1">
                <label htmlFor="">{t('メモ')}</label>
                <textarea
                  // id="memo"privilege_name
                  {...register('memo', {
                    required: false,
                  })}
                  rows={7}
                  cols={33}
                  defaultValue=""
                >
                  {/* {screenParams.memo} */}
                </textarea>
              </div>
              <div className="btnfr btnfr2 mb40">
                <div style={{ width: 'auto' }}>
                  <span className="mr10">
                    <input type="button" value="キャンセル" className="btn1 btnbg-g" onClick={onCancel} />
                  </span>
                  <span className="mr10">
                    <input type="button" id="" name="" value="元に戻す" className="btn1 btnbg-g" onClick={onReset} />
                  </span>
                  <span>
                    {/* <input type="submit" id="" name="" value="更新" className="btn1 lsp1" /> */}
                    <input
                      type="button"
                      id=""
                      name=""
                      value="更新"
                      className="btn1 lsp1"
                      onClick={onUpdate}
                      disabled={!Editable}
                    />
                  </span>
                </div>
              </div>
            </div>
          </form>
        </fieldset>
      </section>
      {showResetModalFlg && (
        <ConfirmModal
          onClickOk={() => {
            void onClickResetOk();
          }}
          onClickCancel={() => setShowResetModalFlg(false)}
        >
          <div className="poptxt">
            <p>{t('初期表示に戻りますがよろしいですか？')}</p>
          </div>
        </ConfirmModal>
      )}

      {showUpdateModalFlg && (
        <ConfirmModal
          onClickOk={() => {
            void onSubmit().then(() => {
              setShowUpdateModalFlg(false);
              setDisplayPutOk(true);
            });
          }}
          onClickCancel={() => setShowUpdateModalFlg(false)}
        >
          <div className="poptxt">
            <p>{t('更新します。よろしいですか？')}</p>
          </div>
        </ConfirmModal>
      )}
      {isSubmitting && <SpinnerModal />}
      {DisplayPutError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayPutError(false);
          }}
          error={putError}
        />
      )}
      {DisplayPutOk && (
        <OkModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayPutOk(false);
          }}
        >
          <div>{t('データを更新しました。')}</div>
        </OkModal>
      )}
    </>
  );
}

interface MainAreaProps {
  privilegeId: number;
}

function MainArea({ privilegeId }: MainAreaProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const options = useDefaultOptions(language);
  const { isLoading, isError, data } = useGetPrivilegesId(privilegeId, options);

  if (isLoading) {
    return <div id="main">{t('お待ちください。')}</div>;
  }

  if (isError || data == null) {
    return <div id="main">{t('エラーが発生しました。')}</div>;
  }

  let privilegeDetail: PrivilegeItemList = {};
  if (data.data.privilege_detail != null) privilegeDetail = data.data.privilege_detail;

  let Editable: boolean;
  Editable = false;
  if (data.data.editable != null) {
    Editable = Boolean(data.data.editable);
  }
  return (
    <div id="main">
      {/* <EditArea privilegeId={privilegeId} screenParams={screenParams} /> */}
      <EditArea privilegeId={privilegeId} screenParams={privilegeDetail} Editable={Editable} />
    </div>
  );
}

function PrivilegesEditPage() {
  const { t } = useTranslation();
  const { privilegeId: privilegeIdString } = useParams();
  if (privilegeIdString == null) {
    return <div id="main">{t('この URL は存在しません。')}</div>;
  }

  const privilegeId = parseInt(privilegeIdString, 10);

  return <MainArea privilegeId={privilegeId} />;
}

export default PrivilegesEditPage;
